import React, { useState, useEffect } from 'react';
import { db } from '../Firebase';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  Package, ClipboardList, PhoneCall, Briefcase, FileText,
  Users, Calendar, ArrowRight, Activity, Loader2, BookOpen, ShieldAlert, Trash2
} from 'lucide-react';
// Helper to extract a Date object from Firestore document submittedAt timestamp
const getDocDate = (item) => {
  if (!item || !item.submittedAt) return null;
  const t = item.submittedAt;
  if (t && typeof t.toDate === 'function') {
    return t.toDate();
  }
  if (t && t.seconds) {
    return new Date(t.seconds * 1000);
  }
  return new Date(t);
};

// Helper to generate list of last 6 months (inclusive of current month)
const generateLast6Months = () => {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: d.getFullYear(),
      month: d.getMonth(),
      label: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }),
      shortLabel: d.toLocaleString('en-US', { month: 'short' }),
      productCount: 0,
      consultationCount: 0
    });
  }
  return months;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    products: 0,
    productEnquiries: 0,
    consultations: 0,
    jobs: 0,
    applications: 0,
    blogs: 0,
    completedProjects: 0,
    upcomingProjects: 0
  });

  const [loading, setLoading] = useState(true);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [recentProductEnquiries, setRecentProductEnquiries] = useState([]);
  const [trendData, setTrendData] = useState(() => generateLast6Months());
  const [hoveredProductIdx, setHoveredProductIdx] = useState(null);
  const [hoveredConsultIdx, setHoveredConsultIdx] = useState(null);

  const [duplicateList, setDuplicateList] = useState([]);
  const [cleaning, setCleaning] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // 1. Get Live Stats Count (Firestore query)
      const productsSnap = await getDocs(collection(db, 'products'));
      const productEnquiriesSnap = await getDocs(collection(db, 'productEnquiries'));
      const consultationsSnap = await getDocs(collection(db, 'consultations'));
      const jobsSnap = await getDocs(collection(db, 'jobs'));
      const applicationsSnap = await getDocs(collection(db, 'job_applications'));
      const blogsSnap = await getDocs(collection(db, 'blogs'));
      const completedProjectsSnap = await getDocs(collection(db, 'completed_projects'));
      const upcomingProjectsSnap = await getDocs(collection(db, 'upcoming_projects'));
      const statsSnap = await getDocs(collection(db, 'stats'));
      const servicesSnap = await getDocs(collection(db, 'services'));
      const categoriesSnap = await getDocs(collection(db, 'product_categories'));

      // Use live database document counts directly
      const productsCount = productsSnap.size;
      const enquiriesCount = productEnquiriesSnap.size;
      const consultationsCount = consultationsSnap.size;
      const jobsCount = jobsSnap.size;
      const appsCount = applicationsSnap.size;
      const blogsCount = blogsSnap.size;
      const completedCount = completedProjectsSnap.size;
      const upcomingCount = upcomingProjectsSnap.size;

      setStats({
        products: productsCount,
        productEnquiries: enquiriesCount,
        consultations: consultationsCount,
        jobs: jobsCount,
        applications: appsCount,
        blogs: blogsCount,
        completedProjects: completedCount,
        upcomingProjects: upcomingCount
      });

      // Calculate duplicates
      const findDuplicates = (docs, getKey) => {
        const seen = new Set();
        const dups = [];
        docs.forEach(docSnap => {
          const data = docSnap.data();
          const key = getKey(data);
          if (key) {
            const norm = key.trim().toLowerCase();
            if (seen.has(norm)) {
              dups.push({ id: docSnap.id, coll: docSnap.ref.parent.id, name: key });
            } else {
              seen.add(norm);
            }
          }
        });
        return dups;
      };

      const dupStats = findDuplicates(statsSnap.docs, d => d.label);
      const dupServices = findDuplicates(servicesSnap.docs, d => d.title);
      const dupProducts = findDuplicates(productsSnap.docs, d => d.name);
      const dupComps = findDuplicates(completedProjectsSnap.docs, d => d.title);
      const dupUps = findDuplicates(upcomingProjectsSnap.docs, d => d.hosp);
      const dupBlogs = findDuplicates(blogsSnap.docs, d => d.title);
      const dupCats = findDuplicates(categoriesSnap.docs, d => d.name);
      const dupJobs = findDuplicates(jobsSnap.docs, d => d.title ? `${d.title}_${d.location || ''}_${d.type || ''}` : null);

      const allDuplicates = [
        ...dupStats,
        ...dupServices,
        ...dupProducts,
        ...dupComps,
        ...dupUps,
        ...dupBlogs,
        ...dupCats,
        ...dupJobs
      ];

      setDuplicateList(allDuplicates);

      // Calculate dynamic month-wise trend stats
      const trendMonths = generateLast6Months();
      const productList = productEnquiriesSnap.docs.map(doc => doc.data());
      const consultationList = consultationsSnap.docs.map(doc => doc.data());

      productList.forEach(item => {
        const date = getDocDate(item) || new Date();
        const itemYear = date.getFullYear();
        const itemMonth = date.getMonth();
        const match = trendMonths.find(m => m.year === itemYear && m.month === itemMonth);
        if (match) {
          match.productCount += 1;
        }
      });

      consultationList.forEach(item => {
        const date = getDocDate(item) || new Date();
        const itemYear = date.getFullYear();
        const itemMonth = date.getMonth();
        const match = trendMonths.find(m => m.year === itemYear && m.month === itemMonth);
        if (match) {
          match.consultationCount += 1;
        }
      });

      setTrendData(trendMonths);

      // 2. Fetch Recent Consultation Enquiries
      const cQuery = query(collection(db, 'consultations'), orderBy('submittedAt', 'desc'), limit(3));
      const cSnap = await getDocs(cQuery);
      const cList = cSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentEnquiries(cList);

      // 3. Fetch Recent Job Applications
      const aQuery = query(collection(db, 'job_applications'), orderBy('submittedAt', 'desc'), limit(3));
      const aSnap = await getDocs(aQuery);
      const aList = aSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentApps(aList);

      // 4. Fetch Recent Product Enquiries
      const pQuery = query(collection(db, 'productEnquiries'), orderBy('submittedAt', 'desc'), limit(3));
      const pSnap = await getDocs(pQuery);
      const pList = pSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentProductEnquiries(pList);

    } catch (err) {
      console.error("Error loading dashboard metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleCleanDatabase = async () => {
    if (duplicateList.length === 0) return;
    
    const result = await Swal.fire({
      title: 'Deduplicate Database?',
      text: `Are you sure you want to delete all ${duplicateList.length} duplicate records? Only unique entries will be kept. This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, clean them!'
    });

    if (!result.isConfirmed) return;

    try {
      setCleaning(true);
      
      // Delete duplicates one by one
      for (const item of duplicateList) {
        const docRef = doc(db, item.coll, item.id);
        await deleteDoc(docRef);
      }

      Swal.fire({
        title: 'Success',
        text: `Successfully removed ${duplicateList.length} duplicate documents!`,
        icon: 'success',
        confirmButtonColor: '#111111'
      });

      setDuplicateList([]);
      // Reload stats
      fetchDashboardData();
    } catch (err) {
      console.error("Error cleaning database duplicates:", err);
      Swal.fire('Error', 'Failed to remove some duplicate documents.', 'error');
    } finally {
      setCleaning(false);
    }
  };


  // Calculations for dynamic SVG Trend Charts
  const maxProductCount = Math.max(
    ...trendData.map(d => d.productCount),
    5
  );

  const maxConsultCount = Math.max(
    ...trendData.map(d => d.consultationCount),
    5
  );

  const productPoints = trendData.map((d, i) => {
    const x = 40 + (i / 5) * 420;
    const y = 160 - (d.productCount / maxProductCount) * 130;
    return { x, y };
  });

  const consultPoints = trendData.map((d, i) => {
    const x = 40 + (i / 5) * 420;
    const y = 160 - (d.consultationCount / maxConsultCount) * 130;
    return { x, y };
  });

  const getLinePath = (points) => {
    if (points.length === 0) return '';
    return points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  };

  const getAreaPath = (points) => {
    if (points.length === 0) return '';
    const line = getLinePath(points);
    return `${line} L ${points[points.length - 1].x} 160 L ${points[0].x} 160 Z`;
  };

  const productLine = getLinePath(productPoints);
  const productArea = getAreaPath(productPoints);

  const consultLine = getLinePath(consultPoints);
  const consultArea = getAreaPath(consultPoints);

  return (
    <div className="space-y-10">

      {/* Upper Welcome Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-full blur-2xl" />
        <div className="space-y-1.5 relative z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins flex items-center gap-1.5">
            <Activity size={14} className="animate-pulse" /> Live System Monitor
          </span>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-montserrat text-brand-navy tracking-tight">Welcome to Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-500 font-poppins">Real-time statistics of product inquiries, hospital consultations, and career tracks.</p>
        </div>
        <div className="bg-brand-navy text-white px-5 py-2.5 rounded-2xl flex items-center gap-2 text-xs font-bold font-poppins shadow-md border border-white/5 relative z-10 shrink-0">
          <Calendar size={14} className="text-brand-green" />
          <span>Last Sync: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Database health alert banner if duplicate records are detected */}
      {duplicateList.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-3xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-red-100/10 rounded-full blur-2xl animate-pulse" />
          <div className="flex items-start gap-4 relative z-10">
            <div className="bg-red-100 text-red-600 p-3 rounded-2xl shrink-0">
              <ShieldAlert size={24} />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold font-montserrat text-brand-navy">Duplicate Records Detected</h3>
              <p className="text-xs text-gray-600 font-poppins leading-relaxed">
                We detected <strong>{duplicateList.length} duplicate entries</strong> in your database collections. This usually happens when the network connection syncs twice or the browser triggers double submit events on creation.
              </p>
            </div>
          </div>
          <button
            onClick={handleCleanDatabase}
            disabled={cleaning}
            className="bg-brand-navy hover:bg-brand-blue text-white text-xs font-bold py-3 px-6 rounded-2xl flex items-center gap-2 transition-all shrink-0 hover:scale-[1.02] disabled:opacity-70 disabled:hover:scale-100 shadow-md relative z-10"
          >
            {cleaning ? (
              <>
                <Loader2 className="animate-spin" size={14} /> Cleaning Database...
              </>
            ) : (
              <>
                <Trash2 size={14} /> Deduplicate Database
              </>
            )}
          </button>
        </motion.div>
      )}

      {/* Numerical Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 font-poppins">
        {/* Card 1: Product Inventory */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(222, 41, 41, 0.08), 0 8px 10px -6px rgba(222, 41, 41, 0.08)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/products')}
        >
          {/* Card background ambient glow on hover */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-blue/5 rounded-full blur-2xl group-hover:bg-brand-blue/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Products Catalog</span>
            <div className="w-10 h-10 rounded-xl bg-brand-blue/10 border border-brand-blue/20 text-brand-blue flex items-center justify-center shrink-0 shadow-sm shadow-brand-blue/5">
              <Package size={18} />
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy group-hover:text-brand-blue transition-colors">
              {loading ? <Loader2 className="animate-spin text-gray-300" size={24} /> : stats.products}
            </h3>
            <p className="text-[11px] text-gray-500 font-medium">Active items in clinical & surgical portfolio</p>
          </div>
        </motion.div>

        {/* Card 2: Product Enquiries */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(229, 62, 62, 0.08), 0 8px 10px -6px rgba(229, 62, 62, 0.08)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/enquiries')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-green/5 rounded-full blur-2xl group-hover:bg-brand-green/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Product Enquiries</span>
            <div className="w-10 h-10 rounded-xl bg-brand-green/10 border border-brand-green/20 text-brand-green flex items-center justify-center shrink-0 shadow-sm shadow-brand-green/5">
              <ClipboardList size={18} />
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy group-hover:text-brand-green transition-colors">
              {loading ? <Loader2 className="animate-spin text-gray-300" size={24} /> : stats.productEnquiries}
            </h3>
            <p className="text-[11px] text-gray-500 font-medium">Pricing quotation requests logged</p>
          </div>
        </motion.div>

        {/* Card 3: Consultation Requests */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.08), 0 8px 10px -6px rgba(245, 158, 11, 0.08)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/consultations')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl group-hover:bg-amber-500/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Consultations</span>
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center shrink-0 shadow-sm shadow-amber-500/5">
              <PhoneCall size={18} />
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy group-hover:text-amber-500 transition-colors">
              {loading ? <Loader2 className="animate-spin text-gray-300" size={24} /> : stats.consultations}
            </h3>
            <p className="text-[11px] text-gray-500 font-medium">Modular OT & turnkey hospital setups planned</p>
          </div>
        </motion.div>

        {/* Card 4: Projects Showcase (Combined Completed & Upcoming) */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(17, 17, 17, 0.04), 0 8px 10px -6px rgba(17, 17, 17, 0.04)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/projects')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-brand-navy/5 rounded-full blur-2xl group-hover:bg-brand-navy/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Projects Pipeline</span>
            <div className="w-10 h-10 rounded-xl bg-brand-navy/10 border border-brand-navy/20 text-brand-navy flex items-center justify-center shrink-0 shadow-sm">
              <Activity size={18} />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="space-y-1 flex-1">
              <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Completed
              </div>
              <h3 className="text-2xl font-extrabold font-montserrat text-brand-navy">
                {loading ? <Loader2 className="animate-spin text-gray-200" size={16} /> : stats.completedProjects}
              </h3>
            </div>
            <div className="h-8 w-px bg-brand-silver" />
            <div className="space-y-1 flex-1">
              <div className="text-[10px] font-bold text-brand-blue uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-blue" /> Upcoming
              </div>
              <h3 className="text-2xl font-extrabold font-montserrat text-brand-navy">
                {loading ? <Loader2 className="animate-spin text-gray-200" size={16} /> : stats.upcomingProjects}
              </h3>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 font-medium mt-3 pt-2 border-t border-brand-silver/40">Clinical showcases & setup roadmap pipeline</p>
        </motion.div>

        {/* Card 5: Recruitment Hub (Combined Posted Jobs & Applications) */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.08), 0 8px 10px -6px rgba(16, 185, 129, 0.08)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/jobs')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Recruitment & Careers</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 flex items-center justify-center shrink-0 shadow-sm shadow-emerald-500/5">
              <Briefcase size={18} />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="space-y-1 flex-1">
              <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Jobs Posted</div>
              <h3 className="text-2xl font-extrabold font-montserrat text-brand-navy">
                {loading ? <Loader2 className="animate-spin text-gray-200" size={16} /> : stats.jobs}
              </h3>
            </div>
            <div className="h-8 w-px bg-brand-silver" />
            <div className="space-y-1 flex-1">
              <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Applications</div>
              <h3 className="text-2xl font-extrabold font-montserrat text-brand-navy">
                {loading ? <Loader2 className="animate-spin text-gray-200" size={16} /> : stats.applications}
              </h3>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 font-medium mt-3 pt-2 border-t border-brand-silver/40">Active openings and job applications log</p>
        </motion.div>

        {/* Card 6: Blogs Published */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.08), 0 8px 10px -6px rgba(139, 92, 246, 0.08)' }}
          className="bg-white p-6 rounded-3xl border border-brand-silver/65 shadow-sm flex flex-col justify-between group relative overflow-hidden cursor-pointer"
          onClick={() => navigate('/admin/blogs')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/5 rounded-full blur-2xl group-hover:bg-violet-500/10 transition-colors duration-300" />
          <div className="flex justify-between items-start mb-4">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">Blogs Portal</span>
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 text-violet-600 flex items-center justify-center shrink-0 shadow-sm shadow-violet-500/5">
              <BookOpen size={18} />
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy group-hover:text-violet-600 transition-colors">
              {loading ? <Loader2 className="animate-spin text-gray-300" size={24} /> : stats.blogs}
            </h3>
            <p className="text-[11px] text-gray-500 font-medium">Articles, clinical news & newslogs published</p>
          </div>
        </motion.div>
      </div>

      {/* Innovative Live SVG Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Left: Product Enquiries Trends Area Chart */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6 relative">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-silver pb-4">
            <div>
              <h3 className="text-base font-bold font-montserrat text-brand-navy flex items-center gap-2">
                <ClipboardList size={18} className="text-[#DE2929]" />
                Product Enquiries Trends
              </h3>
              <p className="text-xs text-gray-400 font-poppins">Monthly product quotation requests volume analysis</p>
            </div>
            
            <span className="text-[10px] font-bold text-brand-blue uppercase tracking-widest bg-brand-blue/10 px-2.5 py-1 rounded font-poppins shrink-0">
              Monthly Breakdown
            </span>
          </div>

          {/* Animated Custom SVG Area Chart */}
          <div className="relative h-64 w-full">
            <svg viewBox="0 0 500 200" className="w-full h-full" preserveAspectRatio="none">
              <defs>
                {/* Product enquiries red gradient */}
                <linearGradient id="productGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#DE2929" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#DE2929" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid Lines */}
              <line x1="40" y1="30" x2="460" y2="30" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />
              <line x1="40" y1="95" x2="460" y2="95" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />
              <line x1="40" y1="160" x2="460" y2="160" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />

              {/* Y-Axis Value Labels (Scale Numbers) */}
              <text x="25" y="34" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">{Math.round(maxProductCount)}</text>
              <text x="25" y="99" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">{Math.round(maxProductCount / 2)}</text>
              <text x="25" y="164" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">0</text>

              {/* Product Area & Stroke */}
              {productArea && (
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  d={productArea}
                  fill="url(#productGrad)"
                />
              )}
              {productLine && (
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  d={productLine}
                  fill="none"
                  stroke="#DE2929"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
              )}

              {/* Key Indicators Circles */}
              {productPoints.map((p, idx) => (
                <circle
                  key={`p-circle-${idx}`}
                  cx={p.x}
                  cy={p.y}
                  r={hoveredProductIdx === idx ? 6 : 4}
                  fill="#FFFFFF"
                  stroke="#DE2929"
                  strokeWidth={hoveredProductIdx === idx ? 3.5 : 2.5}
                  className="transition-all duration-200"
                />
              ))}

              {/* Hover Guideline (Vertical Dashed Line) */}
              {hoveredProductIdx !== null && (
                <line
                  x1={40 + (hoveredProductIdx / 5) * 420}
                  y1={30}
                  x2={40 + (hoveredProductIdx / 5) * 420}
                  y2={160}
                  stroke="#94A3B8"
                  strokeWidth="1.5"
                  strokeDasharray="4"
                />
              )}

              {/* Hover segments overlay */}
              {trendData.map((d, idx) => {
                const x = 40 + (idx / 5) * 420;
                const segmentWidth = 420 / 5;
                return (
                  <rect
                     key={`hover-rect-prod-${idx}`}
                     x={idx === 0 ? 40 : x - segmentWidth / 2}
                     y={0}
                     width={idx === 0 || idx === 5 ? segmentWidth / 2 : segmentWidth}
                     height={200}
                     fill="transparent"
                     className="cursor-pointer"
                     onMouseEnter={() => setHoveredProductIdx(idx)}
                     onMouseLeave={() => setHoveredProductIdx(null)}
                  />
                );
              })}
            </svg>

            {/* Label overlays with monthly counts (Numbers on X-Axis) */}
            <div 
              className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-gray-400 font-poppins font-medium mt-2 pt-2 border-t border-brand-silver"
              style={{ paddingLeft: '40px', paddingRight: '40px' }}
            >
              {trendData.map((d, idx) => (
                <span 
                  key={idx} 
                  className={`flex flex-col items-center ${idx === 5 ? "text-brand-blue font-bold" : ""}`}
                >
                  <span>{d.shortLabel} {idx === 5 ? "(Current)" : ""}</span>
                  <span className="text-[9px] text-[#DE2929] font-bold mt-0.5">({d.productCount})</span>
                </span>
              ))}
            </div>

            {/* Hover Tooltip */}
            {hoveredProductIdx !== null && (
              <div 
                className="absolute bg-brand-navy text-white text-xs rounded-xl p-3 shadow-xl border border-white/10 z-20 pointer-events-none transition-all duration-200"
                style={{
                  left: `${8 + (hoveredProductIdx / 5) * 84}%`,
                  top: '10%',
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="font-bold border-b border-white/10 pb-1 mb-1.5 text-center text-[10px] uppercase tracking-wider text-brand-silver">
                  {trendData[hoveredProductIdx].label}
                </div>
                <div className="flex flex-col gap-1.5 font-poppins">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#DE2929]" />
                    <span>Enquiries: <strong className="text-white text-sm ml-1">{trendData[hoveredProductIdx].productCount}</strong></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Consultation Trends Area Chart */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6 relative">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-silver pb-4">
            <div>
              <h3 className="text-base font-bold font-montserrat text-brand-navy flex items-center gap-2">
                <PhoneCall size={18} className="text-[#F59E0B]" />
                Consultation Trends
              </h3>
              <p className="text-xs text-gray-400 font-poppins">turnkey hospital setups planning volume</p>
            </div>
            
            <span className="text-[10px] font-bold text-brand-blue uppercase tracking-widest bg-brand-blue/10 px-2.5 py-1 rounded font-poppins shrink-0">
              Monthly Breakdown
            </span>
          </div>

          {/* Animated Custom SVG Area Chart */}
          <div className="relative h-64 w-full">
            <svg viewBox="0 0 500 200" className="w-full h-full" preserveAspectRatio="none">
              <defs>
                {/* Consultation enquiries amber gradient */}
                <linearGradient id="consultGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid Lines */}
              <line x1="40" y1="30" x2="460" y2="30" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />
              <line x1="40" y1="95" x2="460" y2="95" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />
              <line x1="40" y1="160" x2="460" y2="160" stroke="#F1F5F9" strokeWidth="1" strokeDasharray="4" />

              {/* Y-Axis Value Labels (Scale Numbers) */}
              <text x="25" y="34" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">{Math.round(maxConsultCount)}</text>
              <text x="25" y="99" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">{Math.round(maxConsultCount / 2)}</text>
              <text x="25" y="164" fill="#94A3B8" fontSize="9" fontFamily="Poppins" textAnchor="end">0</text>

              {/* Consultation Area & Stroke */}
              {consultArea && (
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  d={consultArea}
                  fill="url(#consultGrad)"
                />
              )}
              {consultLine && (
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  d={consultLine}
                  fill="none"
                  stroke="#F59E0B"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
              )}

              {/* Key Indicators Circles */}
              {consultPoints.map((p, idx) => (
                <circle
                  key={`c-circle-${idx}`}
                  cx={p.x}
                  cy={p.y}
                  r={hoveredConsultIdx === idx ? 6 : 4}
                  fill="#FFFFFF"
                  stroke="#F59E0B"
                  strokeWidth={hoveredConsultIdx === idx ? 3.5 : 2.5}
                  className="transition-all duration-200"
                />
              ))}

              {/* Hover Guideline (Vertical Dashed Line) */}
              {hoveredConsultIdx !== null && (
                <line
                  x1={40 + (hoveredConsultIdx / 5) * 420}
                  y1={30}
                  x2={40 + (hoveredConsultIdx / 5) * 420}
                  y2={160}
                  stroke="#94A3B8"
                  strokeWidth="1.5"
                  strokeDasharray="4"
                />
              )}

              {/* Hover segments overlay */}
              {trendData.map((d, idx) => {
                const x = 40 + (idx / 5) * 420;
                const segmentWidth = 420 / 5;
                return (
                  <rect
                     key={`hover-rect-consult-${idx}`}
                     x={idx === 0 ? 40 : x - segmentWidth / 2}
                     y={0}
                     width={idx === 0 || idx === 5 ? segmentWidth / 2 : segmentWidth}
                     height={200}
                     fill="transparent"
                     className="cursor-pointer"
                     onMouseEnter={() => setHoveredConsultIdx(idx)}
                     onMouseLeave={() => setHoveredConsultIdx(null)}
                  />
                );
              })}
            </svg>

            {/* Label overlays with monthly counts (Numbers on X-Axis) */}
            <div 
              className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-gray-400 font-poppins font-medium mt-2 pt-2 border-t border-brand-silver"
              style={{ paddingLeft: '40px', paddingRight: '40px' }}
            >
              {trendData.map((d, idx) => (
                <span 
                  key={idx} 
                  className={`flex flex-col items-center ${idx === 5 ? "text-brand-blue font-bold" : ""}`}
                >
                  <span>{d.shortLabel} {idx === 5 ? "(Current)" : ""}</span>
                  <span className="text-[9px] text-[#F59E0B] font-bold mt-0.5">({d.consultationCount})</span>
                </span>
              ))}
            </div>

            {/* Hover Tooltip */}
            {hoveredConsultIdx !== null && (
              <div 
                className="absolute bg-brand-navy text-white text-xs rounded-xl p-3 shadow-xl border border-white/10 z-20 pointer-events-none transition-all duration-200"
                style={{
                  left: `${8 + (hoveredConsultIdx / 5) * 84}%`,
                  top: '10%',
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="font-bold border-b border-white/10 pb-1 mb-1.5 text-center text-[10px] uppercase tracking-wider text-brand-silver">
                  {trendData[hoveredConsultIdx].label}
                </div>
                <div className="flex flex-col gap-1.5 font-poppins">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                    <span>Consultations: <strong className="text-white text-sm ml-1">{trendData[hoveredConsultIdx].consultationCount}</strong></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Bottom Layout: Recent Logs list widgets */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

        {/* Recent Consultation Requests */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-brand-silver pb-4">
            <h3 className="text-base font-bold font-montserrat text-brand-navy flex items-center gap-2">
              <PhoneCall size={18} className="text-amber-500" />
              Recent Consultations
            </h3>
            <Link to="/admin/consultations" className="text-xs font-bold text-brand-blue hover:text-brand-navy flex items-center gap-1 font-poppins">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="space-y-4">
            {recentEnquiries.length === 0 ? (
              <p className="text-xs text-gray-400 font-poppins italic py-4">No recent consultation inquiries received.</p>
            ) : (
              recentEnquiries.map((enq, idx) => (
                <div key={idx} className="flex gap-4 p-4 rounded-2xl bg-brand-light border border-brand-silver/60 hover:border-brand-blue/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                    <Users size={18} />
                  </div>
                  <div className="min-w-0 flex-grow space-y-1">
                    <div className="flex justify-between items-center">
                      <h4 className="text-xs font-bold text-brand-navy font-montserrat truncate">{enq.name}</h4>
                      <span className="text-[9px] text-gray-400 font-poppins font-medium">
                        {enq.submittedAt ? new Date(enq.submittedAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                      </span>
                    </div>
                    <p className="text-[10px] text-brand-blue font-bold font-poppins truncate uppercase tracking-wider">{enq.hospitalName} — {enq.designation || 'MD'}</p>
                    <p className="text-[11px] text-gray-500 font-poppins line-clamp-1 leading-relaxed mt-1">{enq.message || 'No custom scope specified.'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Latest Product Enquiries */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-brand-silver pb-4">
            <h3 className="text-base font-bold font-montserrat text-brand-navy flex items-center gap-2">
              <Package size={18} className="text-brand-blue" />
              Latest Product Enquiries
            </h3>
            <Link to="/admin/enquiries" className="text-xs font-bold text-brand-blue hover:text-brand-navy flex items-center gap-1 font-poppins">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="space-y-4">
            {recentProductEnquiries.length === 0 ? (
              <p className="text-xs text-gray-400 font-poppins italic py-4">No recent product enquiries received.</p>
            ) : (
              recentProductEnquiries.map((enq, idx) => (
                <div key={idx} className="flex gap-4 p-4 rounded-2xl bg-brand-light border border-brand-silver/60 hover:border-brand-blue/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                    <Package size={18} />
                  </div>
                  <div className="min-w-0 flex-grow space-y-1">
                    <div className="flex justify-between items-center">
                      <h4 className="text-xs font-bold text-brand-navy font-montserrat truncate">{enq.contactName || enq.name}</h4>
                      <span className="text-[9px] text-gray-400 font-poppins font-medium">
                        {enq.submittedAt ? new Date(enq.submittedAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                      </span>
                    </div>
                    <p className="text-[10px] text-brand-blue font-bold font-poppins truncate uppercase tracking-wider">{enq.productName} (Qty: {enq.quantity || 1})</p>
                    <p className="text-[11px] text-gray-500 font-poppins line-clamp-1 leading-relaxed mt-1">{enq.notes || 'No custom scope specified.'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Job Applications */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-brand-silver pb-4">
            <h3 className="text-base font-bold font-montserrat text-brand-navy flex items-center gap-2">
              <FileText size={18} className="text-emerald-500" />
              Latest Applications
            </h3>
            <Link to="/admin/jobs" className="text-xs font-bold text-brand-blue hover:text-brand-navy flex items-center gap-1 font-poppins">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="space-y-4">
            {recentApps.length === 0 ? (
              <p className="text-xs text-gray-400 font-poppins italic py-4">No recent job applications received.</p>
            ) : (
              recentApps.map((app, idx) => (
                <div key={idx} className="flex gap-4 p-4 rounded-2xl bg-brand-light border border-brand-silver/60 hover:border-brand-blue/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                    <FileText size={18} />
                  </div>
                  <div className="min-w-0 flex-grow space-y-1">
                    <div className="flex justify-between items-center">
                      <h4 className="text-xs font-bold text-brand-navy font-montserrat truncate">{app.name}</h4>
                      <span className="text-[9px] text-gray-400 font-poppins font-medium">
                        {app.submittedAt ? new Date(app.submittedAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                      </span>
                    </div>
                    <p className="text-[10px] text-brand-blue font-bold font-poppins truncate uppercase tracking-wider">{app.position} ({app.experience || 'Entry-level'})</p>
                    <p className="text-[11px] text-gray-500 font-poppins line-clamp-1 leading-relaxed mt-1">{app.message || 'No additional note provided.'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
